import asbestosFacilityService from "@/views/asbestosFacility/asbestosFacilityService";
import { apiService, type PagingApiAndNotifierConfig, type Paging } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import { ToastOptionsSuccess, type ToastOptionsError } from "@modernary/mui/components/toasts";
import { apiHosts } from "@/api/constants/apiHosts";

import type {
  AsbestosFacility,
  AsbestosFacilityNew,
  AsbestosFacilityQuery,
  CanDeleteAsbestosFacility,
} from "./asbestosFacility";
import type { AxiosResponse } from "axios";
import { LE } from "@/constants/enums";

const URL = "asbestos-facilities";
const ENTITY_NAME = "Asbestos Facility";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_AQMS_API_BASE_URL,
};
const api = apiService<AsbestosFacility>(apiHosts.AQMS, axiosParams);

const getAsbestosFacilities = (params: AsbestosFacilityQuery, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successToastCb,
    errorToastCb,
  });
};

const getAsbestosFacilityById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successToastCb,
    errorToastCb,
  });
};

const getNewAsbestosFacility = async (ids: AsbestosFacilityIds) => {
  const defaultApp = {
    data: {
      ...asbestosFacilityService.getInitialData(),
      ...ids,
      stateMurn: LE.State.NV,
    },
  } as AxiosResponse<AsbestosFacilityNew, any>;

  return defaultApp;
};

type AsbestosFacilityIds = {
  id?: string | number;
};
const getAsbestosFacilityByIdOrNew = (ids: AsbestosFacilityIds, config: PagingApiAndNotifierConfig = {}) => {
  if (!ids.id) {
    return getNewAsbestosFacility(ids);
  } else {
    return getAsbestosFacilityById(ids.id, config) as Promise<AxiosResponse<AsbestosFacilityNew, any>>;
  }
};

const createAsbestosFacility = (asbestosFacility: AsbestosFacility, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.post(URL, asbestosFacility, {
    successToastCb,
    errorToastCb,
  });
};

const updateAsbestosFacility = (asbestosFacility: AsbestosFacility, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.put(URL + "/" + asbestosFacility.id, asbestosFacility, {
    successToastCb,
    errorToastCb,
  });
};

/**
 * @param {AsbestosFacility} asbestosFacility This cannot be a 'ref' to a asbestosFacility
 */
const saveAsbestosFacility = (asbestosFacility: AsbestosFacility, config = {}) => {
  if (asbestosFacility.id) {
    return updateAsbestosFacility(asbestosFacility, config);
  } else {
    return createAsbestosFacility(asbestosFacility, config);
  }
};

const deleteAsbestosFacilityById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.delete(URL + "/" + id, {
    successToastCb,
    errorToastCb,
  });
};

const canDeleteApi = apiService<CanDeleteAsbestosFacility, Paging<CanDeleteAsbestosFacility>>(
  apiHosts.AQMS,
  axiosParams,
);
const canDeleteAsbestosFacility = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return canDeleteApi.get(URL + "/can-delete/" + id, {
    successToastCb,
    errorToastCb,
  });
};

export {
  deleteAsbestosFacilityById,
  getAsbestosFacilities,
  getAsbestosFacilityById,
  getNewAsbestosFacility,
  getAsbestosFacilityByIdOrNew,
  createAsbestosFacility,
  updateAsbestosFacility,
  saveAsbestosFacility,
  canDeleteAsbestosFacility,
};
